
let __cacheCounter__ = 0;

/**
 * Cache creator with possibility to queue identical requests and resolve them at once.
 */
export default function createCache(options = {}) {
    const cache = new Map();

    // How long does the cache stay alive in ms
    const cacheName = options.name || `Cache-${__cacheCounter__++}`;
    const cacheDebug = options.debug || false;

    const cacheValidTime =
        options.lifetime !== undefined
            ? options.lifetime
            : process.env.REACT_APP_CACHE_LIFETIME !== undefined
            ? parseInt(process.env.REACT_APP_CACHE_LIFETIME)
            : 10000;

    function getCachedResults(key) {
        if (cache.has(key)) {
            const cacheItem = cache.get(key);
            if (cacheItem.finished === true && cacheItem.validUntil > Date.now()) {
                return cacheItem.results;
            }
            if (cacheItem.finished === false && cacheItem.oldValue !== undefined) {
                return cacheItem.oldValue;
            }
        }
        return;
    }

    function getCacheStatus(key) {
        if (cache.has(key)) {
            const cacheItem = cache.get(key);
            if (cacheItem.finished === true) {
                if (cacheItem.validUntil - 10 > Date.now()) {
                    return 'VALID';
                }
                return 'INVALID';
            }
            return 'FETCHING';
        }
        return 'INVALID';
    }

    function isCacheValidForKey(key) {
        if (cache.has(key)) {
            const cacheItem = cache.get(key);
            if (cacheItem.finished === true && cacheItem.validUntil > Date.now()) {
                return true;
            }
        }
        return false;
    }

    function createCachePromise(key, useOldValue = false, extraData = null) {
        if (getCacheStatus(key) === 'FETCHING') {
            return;
        }
        _debug(`:BEGIN: Create cache for key ${key}`);
        const obj = {
            finished: false,
            listeners: [],
        };
        if (extraData !== null) obj.extra = extraData;
        if (useOldValue && isCacheValidForKey(key)) {
            obj.oldValue = getCachedResults(key);
        }

        cache.set(key, obj);
    }

    function waitForTheResults(key) {
        return new Promise((resolve, reject) => {
            _debug(`:WAIT: Wait for key ${key}`);
            if (cache.has(key)) {
                const cacheItem = cache.get(key);
                if (cacheItem.finished === false) {
                    if (cacheItem.listenersCalled !== undefined && cacheItem.sentValue !== undefined) {
                        resolve(cacheItem.sentValue);
                    } else {
                        cacheItem.listeners.push((results, error) => {
                            if (results === null && error !== undefined) {
                                reject(error);
                            } else {
                                resolve(results);
                            }
                        });
                    }
                } else {
                    resolve(getCachedResults(key));
                }
            } else {
                reject({
                    error: `The key ${key} has no promise ongoing`,
                    errCode: 'NOWAIT',
                });
            }
        });
    }

    function triggerResultListeners(key, results, saveValue = false) {
        _debug(`:END: Trigger listeners for key ${key}`);
        const cacheItem = cache.get(key);
        if (!cacheItem) {
            console.warn('createCache: ', key, results, saveValue);
            console.warn('currentCache: ', cache);
            throw Error(`createCache:triggerResultListeners: Cache key: ${key} does not exist. Cache currently has ${cache.size} item(s) in it.`);
        }

        if (cacheItem.listenersCalled === cacheItem.listeners.length) return;

        if (cacheItem !== undefined && cacheItem.listeners !== undefined && cacheItem.listeners.length > 0) {
            cacheItem.listeners.forEach(listener => {
                listener(results);
            });

            cacheItem.listenersCalled = cacheItem.listeners.length;
            cacheItem.sentValue = results;
            cache.set(key, cacheItem);
        }

        if (saveValue) setCacheResults(key, results);
    }

    function setCacheResults(key, results) {
        _debug(`:SET: Set results for key ${key}`);
        cache.set(key, {
            results: results,
            validUntil: Date.now() + cacheValidTime,
            finished: true,
            listeners: [],
        });
    }

    /**
     * Delete cache value and reject all listeners currently waiting for it
     *
     * @param {string} key - Cache Key
     */
    function deleteCacheResult(key = null) {
        _debug(`:DEL: Delete results for key ${key}`);
        if (key !== null && cache.has(key)) {
            const cacheItem = cache.get(key);
            rejectListeners(cacheItem, {
                error: `Cache for key ${key} deleted while waiting for results`,
                errCode: 'DELETE',
            });
            cache.delete(key);
        }
    }

    function resetCache() {
        _debug(`:RESET: Full reset for cache`);
        cache.forEach((cacheItem, key) => {
            rejectListeners(cacheItem, {
                error: 'Cache cleared while waiting for results',
                errCode: 'RESET',
            });
        });
        cache.clear();
    }

    function getExistingValueorWaitForPromise(key) {
        return new Promise((resolve, reject) => {
            if (isCacheValidForKey(key)) {
                resolve(getCachedResults(key));
            }

            waitForTheResults(key)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    function rejectListeners(cacheItem, errObj) {
        if (cacheItem.finished === false && cacheItem.listeners.length > 0) {
            cacheItem.listeners.forEach(listener => {
                listener(null, errObj);
            });
        }
    }

    function getAllCurrentCacheKeysAndTheirStatuses() {
        return Array.from(cache).reduce((keys, [ckey, cobj]) => {
            keys.push([ckey, cobj]);
            return keys;
        }, []);
    }

    function getExtraDataFromCache(key) {
        if (cache.has(key)) {
            const cacheItem = cache.get(key);
            return cacheItem.extra;
        }
    }

    function _debug(txt = '') {
        if (cacheDebug) console.warn(`${cacheName}:CACHEDEBUG: ${txt}`);
    }

    return {
        get: getCachedResults,
        set: setCacheResults,
        del: deleteCacheResult,

        begin: createCachePromise,
        wait: waitForTheResults,
        end: triggerResultListeners,

        keys: getAllCurrentCacheKeysAndTheirStatuses,
        cancel: rejectListeners,

        asyncGet: getExistingValueorWaitForPromise,
        data: getExtraDataFromCache,

        valid: isCacheValidForKey,
        status: getCacheStatus,
        reset: resetCache,
    };
}
